import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Camera, Loader2, ArrowLeft, X } from 'lucide-react';
import { getMatchPhotos, getTeams, getMatches } from '../lib/api';

const PhotoCard = ({ photo, teams, matches, onOpen }) => {
  const teamNames = (photo.team_ids || [])
    .map((id) => teams.find((t) => t.id === id)?.name)
    .filter(Boolean);
  const matchInfo = photo.match_id ? matches.find((m) => m.id === photo.match_id) : null;
  return (
    <button
      type="button"
      onClick={() => onOpen(photo)}
      className="group relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60 hover:border-primary/40 transition-all text-left"
      data-testid={`photo-card-${photo.id}`}
    >
      <div className="aspect-square w-full overflow-hidden">
        <img
          src={`data:${photo.photo_content_type};base64,${photo.photo_data}`}
          alt={photo.title || 'Foto partita'}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
      </div>
      {(photo.title || teamNames.length || matchInfo || photo.taken_at) && (
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/95 via-slate-950/60 to-transparent p-3">
          {photo.title ? <p className="font-heading font-semibold text-sm truncate">{photo.title}</p> : null}
          {teamNames.length ? (
            <p className="text-xs text-primary truncate">{teamNames.join(' · ')}</p>
          ) : null}
          {photo.taken_at ? <p className="text-[10px] text-slate-400">{photo.taken_at}</p> : null}
        </div>
      )}
    </button>
  );
};

const PhotoModal = ({ photo, teams, onClose }) => {
  if (!photo) return null;
  const teamNames = (photo.team_ids || [])
    .map((id) => teams.find((t) => t.id === id)?.name)
    .filter(Boolean);
  return (
    <div
      className="fixed inset-0 z-[100] bg-slate-950/95 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="photo-modal"
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 p-2 rounded-full bg-slate-900 border border-slate-700 hover:bg-slate-800 transition-colors"
        aria-label="Chiudi"
        data-testid="photo-modal-close"
      >
        <X className="w-5 h-5" />
      </button>
      <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
        <img
          src={`data:${photo.photo_content_type};base64,${photo.photo_data}`}
          alt={photo.title || 'Foto partita'}
          className="w-full max-h-[80vh] object-contain rounded-xl"
        />
        <div className="mt-4 text-center space-y-1">
          {photo.title ? <h3 className="font-heading text-xl font-bold">{photo.title}</h3> : null}
          {teamNames.length ? <p className="text-primary text-sm">{teamNames.join(' · ')}</p> : null}
          {photo.description ? <p className="text-slate-400 text-sm">{photo.description}</p> : null}
          {photo.taken_at ? <p className="text-xs text-slate-500">{photo.taken_at}</p> : null}
        </div>
      </div>
    </div>
  );
};

export const GalleriaPage = () => {
  const [photos, setPhotos] = useState([]);
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    document.title = 'Galleria foto partite - Torneo Notturno Viadana Bresciana';
    Promise.all([getMatchPhotos(200), getTeams(), getMatches()])
      .then(([p, t, m]) => {
        setPhotos(p.data || []);
        setTeams(t.data || []);
        setMatches(m.data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="py-8 sm:py-12 px-4 min-h-screen" data-testid="galleria-page">
      <div className="max-w-6xl mx-auto">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-primary mb-4 transition-colors"
          data-testid="back-home-link"
        >
          <ArrowLeft className="w-3 h-3" /> Torna alla Home
        </Link>

        <div className="text-center mb-10 sm:mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-4">
            <Camera className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold text-primary uppercase tracking-wider">
              Galleria del torneo
            </span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black">
            FOTO <span className="text-primary">PARTITE</span>
          </h1>
          <p className="mt-3 text-slate-400 text-sm sm:text-base max-w-xl mx-auto">
            Le immagini più belle del torneo. Aggiornata partita dopo partita.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : photos.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <Camera className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-bold mb-2">Galleria in arrivo</h3>
            <p className="text-slate-400">Le foto delle partite saranno pubblicate qui durante il torneo.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {photos.map((p) => (
              <PhotoCard key={p.id} photo={p} teams={teams} matches={matches} onOpen={setActive} />
            ))}
          </div>
        )}
      </div>
      <PhotoModal photo={active} teams={teams} onClose={() => setActive(null)} />
    </div>
  );
};

export default GalleriaPage;
